/**
 * Step-by-step instructions for an Organisation Maintenance Officer (OMO) to
 * request a NASH PKI Site Certificate via PRODA / HPOS, transcribed from
 * "ScriptPad NASH PKI Guide" (V2.0, September 2018, SWSPHN).
 *
 * Rendered inside the scrollable instruction frame on Step 3(a) of the
 * onboarding wizard.
 */
export interface ProdaStep {
  number: number;
  title: string;
  body: string;
}

export const PRODA_STEPS: ProdaStep[] = [
  {
    number: 1,
    title: 'Open the HPOS home page',
    body: 'Go to the Health Professionals Online Services (HPOS) home page at https://www.servicesaustralia.gov.au/hpos and click "Log on using PRODA".',
  },
  {
    number: 2,
    title: 'Sign in to PRODA',
    body: 'Enter the username and password you created during PRODA registration, then click the Login button.',
  },
  {
    number: 3,
    title: 'Enter the verification code',
    body: 'A verification code is sent to your nominated device (email or SMS). Enter the code in the Verification code field and click Submit.',
  },
  {
    number: 4,
    title: 'Accept the terms (first time only)',
    body: 'If this is your first time logging in via PRODA, tick "I agree with the terms and conditions" to be taken to the HPOS main menu.',
  },
  {
    number: 5,
    title: 'Open your linked service',
    body: 'Click "Go to service" on the My linked services page.',
  },
  {
    number: 6,
    title: 'Choose the organisation',
    body: 'On the HPOS main menu, click "Providers" in the top right corner and select the organisation you would like to act on behalf of.',
  },
  {
    number: 7,
    title: 'Open My programs',
    body: 'From the HPOS main menu, click "My programs".',
  },
  {
    number: 8,
    title: 'Open Healthcare Identifiers and My Health Record',
    body: 'Click the "Healthcare Identifiers and My Health Record" tile.',
  },
  {
    number: 9,
    title: 'Manage existing records',
    body: 'Click "Healthcare Identifiers - Manage existing Records".',
  },
  {
    number: 10,
    title: 'Select your organisation',
    body: 'Choose the organisation you wish to request the certificate for.',
  },
  {
    number: 11,
    title: 'Open the Certificates tab',
    body: 'Click the "Certificates" tab. If you are renewing an existing certificate, click the "Renew" link in the Action column. If you are requesting a NASH PKI certificate for the first time, click "Request a NASH PKI site certificate".',
  },
  {
    number: 12,
    title: 'Provide an SMS contact number',
    body: 'Enter the mobile number you would like to receive the SMS notification on when your certificate is ready. Accept the Terms & Conditions and click "Save Changes".',
  },
  {
    number: 13,
    title: 'Submit the request',
    body: 'Click the "Submit" button to lodge the request.',
  },
  {
    number: 14,
    title: 'Wait for the SMS',
    body: 'You will receive an SMS notification (typically within 15 minutes) confirming the certificate is ready and providing the PIC (password). The SMS reads:\n\n"Your NASH certificate for HPI-O XXXXXX is ready to download through HPOS. It is available for 30 days. Your PIC is XXXXXXXX. Do not reply by SMS."',
  },
  {
    number: 15,
    title: 'Download the certificate',
    body: 'Return to the "Certificates" tab in HPOS and click the "Download" link to download the site.p12 file.',
  },
  {
    number: 16,
    title: 'You are ready',
    body: 'Once you have the site.p12 file and the PIC from the SMS, return here and continue to the next step.',
  },
];
